'use client';

import { useState, useEffect } from 'react';
import { ShieldCheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface CookieConsentBannerProps {
  currentLanguage: string;
}

const CookieConsentBanner = ({ currentLanguage }: CookieConsentBannerProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('gencarbon_cookie_consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    const consentData = {
      accepted: true,
      timestamp: new Date().toISOString(),
      essential: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('gencarbon_cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
    
    // Track consent acceptance
    if (typeof window !== 'undefined') {
      const userMetadata = {
        consentGiven: true,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        language: navigator.language,
      };
      console.log('User Consent Recorded:', userMetadata);
    }
  };

  const handleReject = () => {
    const consentData = {
      accepted: false,
      timestamp: new Date().toISOString(),
      essential: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('gencarbon_cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const content = {
    en: {
      title: 'DPDP Compliance - Cookie Consent',
      description: 'We use cookies to ensure secure operations and improve your experience in compliance with India\'s Digital Personal Data Protection Act 2023.',
      details: 'We collect: Login data, IP addresses, location data, session metadata for security and service improvement. Your data is encrypted and stored securely.',
      accept: 'Accept All',
      reject: 'Essential Only',
      viewDetails: 'View Details',
      hideDetails: 'Hide Details',
    },
    hi: {
      title: 'DPDP अनुपालन - कुकी सहमति',
      description: 'हम भारत के डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम 2023 के अनुपालन में सुरक्षित संचालन सुनिश्चित करने और आपके अनुभव को बेहतर बनाने के लिए कुकीज़ का उपयोग करते हैं।',
      details: 'हम एकत्र करते हैं: लॉगिन डेटा, आईपी पते, स्थान डेटा, सुरक्षा और सेवा सुधार के लिए सत्र मेटाडेटा। आपका डेटा एन्क्रिप्टेड और सुरक्षित रूप से संग्रहीत है।',
      accept: 'सभी स्वीकार करें',
      reject: 'केवल आवश्यक',
      viewDetails: 'विवरण देखें',
      hideDetails: 'विवरण छुपाएं',
    },
    te: {
      title: 'DPDP అనుగుణత - కుకీ సమ్మతి',
      description: 'భారతదేశ డిజిటల్ వ్యక్తిగత డేటా రక్షణ చట్టం 2023కి అనుగుణంగా సురక్షిత కార్యకలాపాలను నిర్ధారించడానికి మరియు మీ అనుభవాన్ని మెరుగుపరచడానికి మేము కుకీలను ఉపయోగిస్తాము.',
      details: 'మేము సేకరిస్తాము: లాగిన్ డేటా, IP చిరునామాలు, స్థాన డేటా, భద్రత మరియు సేవా మెరుగుదల కోసం సెషన్ మెటాడేటా. మీ డేటా ఎన్క్రిప్ట్ చేయబడింది మరియు సురక్షితంగా నిల్వ చేయబడింది.',
      accept: 'అన్నీ అంగీకరించు',
      reject: 'అవసరమైనవి మాత్రమే',
      viewDetails: 'వివరాలు చూడండి',
      hideDetails: 'వివరాలు దాచండి',
    },
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-emerald-900 to-green-900 text-white shadow-2xl border-t-4 border-emerald-400">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <ShieldCheckIcon className="h-8 w-8 text-emerald-300 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">{lang.title}</h3>
              <p className="text-emerald-100 mb-3">{lang.description}</p>
              
              {showDetails && (
                <div className="bg-emerald-800 bg-opacity-50 p-4 rounded-lg mb-3">
                  <p className="text-sm text-emerald-50">{lang.details}</p>
                </div>
              )}
              
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-emerald-300 hover:text-emerald-200 underline mb-4"
              >
                {showDetails ? lang.hideDetails : lang.viewDetails}
              </button>
            </div>
          </div>
          
          <button
            onClick={() => setShowBanner(false)}
            className="text-emerald-300 hover:text-white transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            onClick={handleAccept}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg transition-colors shadow-lg"
          >
            {lang.accept}
          </button>
          <button
            onClick={handleReject}
            className="px-6 py-3 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors"
          >
            {lang.reject}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;